import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Plus } from 'lucide-react';
import { api } from '@/api/api-client';
import { useDialer } from '@/contexts/DialerContext';
import { phoneSuffix } from '@/services/dialer/inboundSms';
import { formatDate } from '@/i18n/locale';

/**
 * Les conversations SMS, la plus récente d'abord.
 *
 * L'écran SMS ouvrait sur un formulaire d'envoi et une liste de prospets à
 * fouiller. Or on reprend une conversation bien plus souvent qu'on n'en
 * commence une : ce qu'on cherche en arrivant, c'est « qui m'a répondu ».
 *
 * Une pastille marque les fils dont le DERNIER message vient du prospet :
 * ceux-là attendent une réponse, et c'est la seule information qui décide de
 * l'ordre dans lequel on les traite.
 */

interface Thread {
  prospect_phone: string;
  last_at?: string;
  last_body?: string;
  last_direction?: string;
  total: number;
  inbound: number;
}

interface SmsConversationsProps {
  /** Le numéro du fil ouvert, pour le marquer dans la liste. */
  selectedPhone?: string | null;
  onSelect: (phone: string, prospectId?: string, name?: string) => void;
  /** Starts a new message: shown in the header when a conversation is open. */
  onCompose?: () => void;
}

export default function SmsConversations({ selectedPhone, onSelect, onCompose }: SmsConversationsProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { prospectQueue } = useDialer() as { prospectQueue?: Array<Record<string, unknown>> };

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ['sms-threads'],
    queryFn: async () => {
      const res = await api.functions.invoke('listSmsThreads', { limit: 50 });
      const data = (res?.data ?? {}) as { threads?: Thread[] };
      return data.threads || [];
    },
    refetchInterval: 15000,
  });

  // Le numéro seul ne dit rien ; le nom vit dans la file d'appel.
  const nameFor = useMemo(() => {
    const index = new Map<string, string>();
    for (const p of prospectQueue || []) {
      const suffix = phoneSuffix(p.prospect_phone as string);
      const name = ((p.prospect_name as string) || '').trim();
      if (suffix && name && !index.has(suffix)) index.set(suffix, name);
    }
    return (phone: string) => index.get(phoneSuffix(phone)) || '';
  }, [prospectQueue]);

  const prospectIdFor = useMemo(() => {
    const index = new Map<string, string>();
    for (const p of prospectQueue || []) {
      const suffix = phoneSuffix(p.prospect_phone as string);
      if (suffix && !index.has(suffix)) index.set(suffix, p.id as string);
    }
    return (phone: string) => index.get(phoneSuffix(phone));
  }, [prospectQueue]);

  if (isLoading) {
    return <div className="h-40 rounded-xl bg-muted animate-pulse" />;
  }

  if (threads.length === 0) {
    return (
      <div className="bg-card border border-border/50 rounded-xl p-6 text-center">
        <MessageSquare className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">{t('pages:sms.noConversation')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden" data-testid="conversations-sms">
      <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2">
        <p className="text-sm font-semibold text-foreground">{t('pages:sms.conversations')}</p>
        {onCompose && selectedPhone && (
          <button
            type="button"
            onClick={onCompose}
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-[var(--vf-accent-text)] hover:underline"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('pages:sms.compose')}
          </button>
        )}
      </div>
      {/* The thin bar that shows on hover (Kevin, 2026-10-07: the default
          one was too big), and as tall as the open conversation. */}
      <div className="divide-y divide-border/50 max-h-[70vh] overflow-y-auto vf-scroll-discreet">
        {threads.map((thread) => {
          const name = nameFor(thread.prospect_phone);
          const waiting = thread.last_direction === 'inbound';
          const selected = !!selectedPhone && phoneSuffix(selectedPhone) === phoneSuffix(thread.prospect_phone);
          return (
            <button
              key={thread.prospect_phone}
              type="button"
              onClick={() => onSelect(thread.prospect_phone, prospectIdFor(thread.prospect_phone), name)}
              className={`w-full text-left px-4 py-3 transition-colors hover:bg-muted/50 ${selected ? 'bg-muted/60' : ''}`}
            >
              <div className="flex items-center gap-2">
                {waiting && (
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: 'var(--vf-green)' }}
                    title={t('pages:sms.waitingReply')}
                  />
                )}
                <span className="text-sm font-medium text-foreground truncate">
                  {name || thread.prospect_phone}
                </span>
                <span className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap">
                  {thread.last_at ? formatDate(thread.last_at, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {thread.last_direction === 'outbound' ? `${t('pages:sms.youPrefix')} ` : ''}
                {thread.last_body || ''}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
